import { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../contexts/AuthContext'
import { FiRefreshCw, FiClock, FiCheckCircle } from 'react-icons/fi'

// Aviso de reinício do sistema (deploy em prod não forçado). Faz POLLING de
// /api/deploy-vote/active (o fin não tem socket) — que também é heartbeat pra
// contar os logados. Quando há votação ativa, mostra um modal com contagem de
// 120s: "autorizar reinício agora" ou "preciso de 2 minutos". O voto não cancela
// o deploy, só decide agora × ao fim da janela. Montado uma vez, no App.

type ActiveVote = { id: string; message: string; deadline: number; resolved: boolean; myVote: 'now' | 'wait' | null }

export function DeployVoteWatcher() {
  const { user, authenticatedFetch } = useAuth()
  const [vote, setVote] = useState<ActiveVote | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [casting, setCasting] = useState(false)

  // Poll enquanto logado. 6s: rápido o bastante pro modal aparecer dentro da
  // janela de 120s, leve o bastante pra não pesar no /verify do crm.
  useEffect(() => {
    if (!user) { setVote(null); return }
    let dead = false
    const poll = async () => {
      try {
        const r = await authenticatedFetch('/api/deploy-vote/active')
        const d = await r.json()
        if (dead) return
        setVote(d?.active ? { id: d.id, message: d.message, deadline: d.deadline, resolved: !!d.resolved, myVote: d.myVote ?? null } : null)
      } catch { /* mantém o último estado */ }
    }
    poll()
    const iv = setInterval(poll, 6000)
    return () => { dead = true; clearInterval(iv) }
  }, [user, authenticatedFetch])

  // Ticker de 1s só enquanto há modal (contagem regressiva).
  useEffect(() => {
    if (!vote) return
    setNow(Date.now())
    const iv = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(iv)
  }, [vote])

  const cast = useCallback(async (choice: 'now' | 'wait') => {
    setCasting(true)
    try {
      await authenticatedFetch('/api/deploy-vote/cast', { method: 'POST', body: JSON.stringify({ choice }) })
      setVote(v => (v ? { ...v, myVote: choice } : v))
    } catch { /* ignora; o próximo poll reconcilia */ } finally { setCasting(false) }
  }, [authenticatedFetch])

  if (!vote) return null
  const rem = Math.max(0, Math.ceil((vote.deadline - now) / 1000))

  // JÁ VOTOU (ou resolvido): NÃO bloqueia a tela — vira um cartão no canto com o
  // timer correndo, pra pessoa terminar o que estava fazendo nos 2 minutos (mandar
  // mensagem, clicar em algo). Pedido do diretor 2026-07-23.
  if (vote.myVote || vote.resolved) {
    return createPortal(
      <div className="fixed bottom-4 right-4 max-w-[calc(100vw-2rem)]" style={{ zIndex: 100000 }}>
        <div className="neu-card glass-panel rounded-xl border border-border p-3 w-72 max-w-full">
          {vote.resolved ? (
            <div className="flex items-center gap-2">
              <FiRefreshCw className="w-4 h-4 animate-spin text-primary shrink-0" />
              <span className="text-xs text-foreground">Reiniciando o sistema…</span>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <FiClock className="w-4 h-4 text-primary shrink-0" />
                <p className="text-xs text-foreground flex-1 min-w-0">
                  Reinício em <span className="font-bold tabular-nums text-primary">{rem}s</span>
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {vote.myVote === 'now' ? 'Você autorizou o reinício agora.' : 'Termine suas pendências — pode usar o sistema normalmente.'}
              </p>
              {vote.myVote === 'wait' && (
                <button type="button" disabled={casting} onClick={() => cast('now')}
                  className="neu-relief mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium px-3 py-1.5 hover:opacity-90 disabled:opacity-60">
                  <FiCheckCircle className="w-3.5 h-3.5" /> Já terminei — reiniciar agora
                </button>
              )}
            </>
          )}
        </div>
      </div>,
      document.body,
    )
  }

  // AINDA NÃO VOTOU: modal central — precisa da decisão. É breve (um clique).
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 100000, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}>
      <div className="neu-card glass-panel rounded-2xl border border-border w-full max-w-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <FiClock className="w-5 h-5 text-primary shrink-0" />
          <h2 className="text-base font-semibold text-foreground">Reinício do sistema</h2>
          <span className="ml-auto tabular-nums text-lg font-bold text-primary">{rem}s</span>
        </div>
        <p className="text-sm text-foreground mb-1">O administrador vai aplicar melhorias e reiniciar o sistema:</p>
        <p className="text-sm text-muted-foreground mb-3 whitespace-pre-line">{vote.message}</p>
        <p className="text-xs text-muted-foreground mb-4">
          Ao escolher, a tela é liberada e a contagem segue num canto — dá tempo de terminar o que estiver fazendo (um lançamento, uma conversa) antes do reinício.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <button type="button" disabled={casting} onClick={() => cast('now')}
            className="neu-relief flex-1 whitespace-nowrap inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium px-3 py-2.5 hover:opacity-90 disabled:opacity-60">
            <FiCheckCircle className="w-4 h-4 shrink-0" /> Autorizar reinício agora
          </button>
          <button type="button" disabled={casting} onClick={() => cast('wait')}
            className="neu-convex flex-1 whitespace-nowrap rounded-lg border border-border text-foreground text-sm font-medium px-3 py-2.5 hover:bg-secondary disabled:opacity-60">
            Preciso de 2 minutos
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
